import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'next-i18next';
import { Modal, Button } from '@components/common';
import { useState } from 'react';
import { fetcher } from '@utils/data-fetching';
import { LOCAL_STORAGE_KEYS } from '@utils/local-storage/localStorage';

interface RejectionReasonModalProps {
  locale: Locale;
  applicantId: number;
}

const REJECTION_REASON_KEYS = [
  'rejection.reason.not_enough_experience',
  'rejection.reason.salary_expectations',
  'rejection.reason.not_matching_skills',
] as const;

/**
 * @description Modal to ask employers why they rejected a recommended applicant with a matching score > 60%.
 * Triggered once per day per device.
 */
export const RejectionReasonModal = NiceModal.create((props: RejectionReasonModalProps) => {
  const { locale, applicantId } = props;
  const modal = useModal();
  const { t } = useTranslation('common');

  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [otherReason, setOtherReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const closeModal = () => {
    void modal.hide();
  };

  const handleSubmit = async () => {
    let reason: string | null = null;
    if (selectedReason === 'other') {
      reason = otherReason.trim();
    } else if (selectedReason) {
      reason = t(selectedReason);
    }
    if (!reason) return;

    setIsSubmitting(true);
    try {
      await fetcher('/v1/applicant-rejection-reason', locale, {
        method: 'POST',
        payload: {
          applicantId,
          reason,
        },
        stringifyPayload: true,
      });

      // Mark as shown today so we don't ask again on this device
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.rejectionReasonLastShown,
        new Date().toDateString(),
      );
    } catch {
      // Silently fail - this is a non-critical feedback feature
    } finally {
      setIsSubmitting(false);
      closeModal();
    }
  };

  return (
    <Modal
      isOpen={modal.visible}
      onClose={closeModal}
      size="sm"
      centered
    >
      <Modal.Header onClose={closeModal}>
        <h3>{t('rejection.reason.modal.title')}</h3>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-4 text-sm text-ink/70">
          {t('rejection.reason.modal.subtitle')}
        </p>

        <div className="flex flex-col gap-2">
          {REJECTION_REASON_KEYS.map((reasonKey) => (
            <label
              key={reasonKey}
              className="flex cursor-pointer items-center gap-2 rounded-md border p-3 transition-colors hover:bg-slate-50"
            >
              <input
                type="radio"
                name="rejectionReason"
                value={reasonKey}
                checked={selectedReason === reasonKey}
                onChange={() => setSelectedReason(reasonKey)}
                className="accent-primary"
              />
              <span className="text-sm">{t(reasonKey)}</span>
            </label>
          ))}

          {/* Other - free input option */}
          <label
            className="flex cursor-pointer items-center gap-2 rounded-md border p-3 transition-colors hover:bg-slate-50"
          >
            <input
              type="radio"
              name="rejectionReason"
              value="other"
              checked={selectedReason === 'other'}
              onChange={() => setSelectedReason('other')}
              className="accent-primary"
            />
            <span className="text-sm">{t('rejection.reason.other')}</span>
          </label>

          {selectedReason === 'other' && (
            <textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder={t('rejection.reason.other.placeholder')}
              className="mt-1 w-full rounded-md border p-2 text-sm focus:border-primary focus:outline-none"
              rows={3}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-center">
          <Button
            color="primary"
            rounding="full"
            onClick={() => { void handleSubmit(); }}
            disabled={isSubmitting || (!selectedReason || (selectedReason === 'other' && !otherReason.trim()))}
          >
            {t('global.action.send')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
});

/**
 * @description Checks conditions and shows the rejection reason modal if applicable.
 * Conditions: matching score > 60%, shown max once per day per device.
 */
export const showRejectionReasonModal = (locale: Locale, applicantId: number, matchingScore?: number) => {
  // Only show if matching score is greater than 60
  if (!matchingScore || matchingScore < 60) return;

  // Check if already shown today on this device
  const lastShown = localStorage.getItem(LOCAL_STORAGE_KEYS.rejectionReasonLastShown);
  if (lastShown === new Date().toDateString()) return;

  void NiceModal.show(RejectionReasonModal, {
    locale,
    applicantId,
  });
};
